import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useOutletContext } from 'react-router-dom';
import { getBasketballPlayerById } from '../../utils/basketballDataService';
import {
  ArrowLeft,
  User,
  TrendingUp,
  Target,
  Activity,
  Shield,
  Zap,
  Award,
  Heart,
  Moon,
  Battery,
  Watch,
  RefreshCw,
  AlertCircle,
  Wifi,
  WifiOff,
} from 'lucide-react';
import { C } from '../../components/design-system/primitives/constants';
import DemoModeBanner from '../../components/DemoModeBanner';

/**
 * BasketballPlayerPage - Individual player profile page
 * SLAM OLED Design System — C constants for all colors
 */

// Position-based stat configurations
const POSITION_STATS = {
  // Guards (PG, SG, G)
  guard: {
    primary: [
      { key: 'ppg', label: 'PPG', icon: Target, highlight: true },
      { key: 'apg', label: 'APG', icon: Activity, highlight: true },
      { key: 'spg', label: 'SPG', icon: Shield },
    ],
    secondary: [
      { key: 'threePct', label: '3P%', format: 'pct' },
      { key: 'ftPct', label: 'FT%', format: 'pct' },
      { key: 'rpg', label: 'RPG' },
    ],
  },
  // Forwards (SF, PF, F)
  forward: {
    primary: [
      { key: 'ppg', label: 'PPG', icon: Target, highlight: true },
      { key: 'rpg', label: 'RPG', icon: TrendingUp, highlight: true },
      { key: 'apg', label: 'APG', icon: Activity },
    ],
    secondary: [
      { key: 'fgPct', label: 'FG%', format: 'pct' },
      { key: 'threePct', label: '3P%', format: 'pct' },
      { key: 'bpg', label: 'BPG' },
    ],
  },
  // Centers (C)
  center: {
    primary: [
      { key: 'ppg', label: 'PPG', icon: Target, highlight: true },
      { key: 'rpg', label: 'RPG', icon: TrendingUp, highlight: true },
      { key: 'bpg', label: 'BPG', icon: Shield },
    ],
    secondary: [
      { key: 'fgPct', label: 'FG%', format: 'pct' },
      { key: 'ftPct', label: 'FT%', format: 'pct' },
      { key: 'apg', label: 'APG' },
    ],
  },
};

// Position color mapping — inline-style compatible hex values
const POSITION_COLORS = {
  PG: { hex: '#3B82F6', bg: '#3B82F633', text: '#60A5FA', border: '#3B82F64D', glow: 'rgba(59, 130, 246, 0.4)' },
  SG: { hex: '#22C55E', bg: '#22C55E33', text: '#4ADE80', border: '#22C55E4D', glow: 'rgba(34, 197, 94, 0.4)' },
  SF: { hex: '#F59E0B', bg: '#F59E0B33', text: '#FBBF24', border: '#F59E0B4D', glow: 'rgba(245, 158, 11, 0.4)' },
  PF: { hex: '#F97316', bg: '#F9731633', text: '#FB923C', border: '#F973164D', glow: 'rgba(249, 115, 22, 0.4)' },
  C: { hex: '#EF4444', bg: '#EF444433', text: '#F87171', border: '#EF44444D', glow: 'rgba(239, 68, 68, 0.4)' },
  G: { hex: '#3B82F6', bg: '#3B82F633', text: '#60A5FA', border: '#3B82F64D', glow: 'rgba(59, 130, 246, 0.4)' },
  F: { hex: '#F97316', bg: '#F9731633', text: '#FB923C', border: '#F973164D', glow: 'rgba(249, 115, 22, 0.4)' },
};

// Mock biometrics data (in production would come from backend)
const MOCK_BIOMETRICS = {
  'trae-young': { whoopConnected: true, ouraConnected: true, readiness: 88, strain: 14.2, hrv: 72, sleep: 7.8 },
  'dejounte-murray': { whoopConnected: true, ouraConnected: false, readiness: 92, strain: 12.5, hrv: 78, sleep: 8.1 },
  '005928': { whoopConnected: true, ouraConnected: true, readiness: 92, strain: 11.8, hrv: 78, sleep: 8.2 },
  '003108': { whoopConnected: true, ouraConnected: true, readiness: 91, strain: 15.1, hrv: 70, sleep: 7.6 },
};

const GlassCard = ({ children, className = '', glow = false, glowColor = `${C.gold}1A` }) => (
  <div
    className={`relative rounded-[14px] backdrop-blur-xl ${className}`}
    style={{
      background: `${C.s1}CC`,
      border: `1px solid ${C.border}`,
      boxShadow: glow
        ? `0 0 40px ${glowColor}, 0 4px 24px rgba(0,0,0,0.3)`
        : '0 4px 24px rgba(0,0,0,0.3)',
    }}
  >
    {children}
  </div>
);

const NeonStat = ({ value, label, color = 'gold', size = 'md', icon: Icon }) => {
  const colorMap = {
    gold: { hex: C.gold },
    green: { hex: C.elite },
    yellow: { hex: C.gold },
    red: { hex: C.dev },
    blue: { hex: C.gen },
    purple: { hex: '#a855f7' },
  };
  const cl = colorMap[color] || colorMap.gold;
  const fontSize = size === 'lg' ? 36 : size === 'sm' ? 20 : 30;

  return (
    <div className="rounded-[14px] p-4 text-center" style={{ background: `${cl.hex}15`, border: `1px solid ${C.border}` }}>
      {Icon && (
        <div className="flex justify-center mb-2">
          <Icon size={size === 'lg' ? 24 : 18} style={{ color: cl.hex, filter: `drop-shadow(0 0 20px ${cl.hex}80)` }} />
        </div>
      )}
      <div style={{ fontSize, fontWeight: 200, color: cl.hex, textShadow: `0 0 20px ${cl.hex}80` }}>
        {value}
      </div>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5, color: C.t3, marginTop: 4 }}>{label}</div>
    </div>
  );
};

const SecondaryStat = ({ value, label }) => (
  <div className="p-3 rounded-[14px] text-center" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
    <div style={{ fontSize: 20, fontWeight: 300, color: C.text }}>{value}</div>
    <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5, color: C.t3, marginTop: 4 }}>{label}</div>
  </div>
);

const ShootingBar = ({ label, value, color = 'gold' }) => {
  const colorMap = {
    gold: { hex: C.gold },
    green: { hex: C.elite },
    blue: { hex: C.gen },
  };
  const cl = colorMap[color] || colorMap.gold;
  const numValue = parseFloat(value) || 0;

  return (
    <div>
      <div className="flex justify-between text-sm mb-2">
        <span style={{ color: C.t2 }}>{label}</span>
        <span style={{ color: C.text, fontWeight: 500 }}>{value ? `${value}%` : '--'}</span>
      </div>
      <div className="h-0.5 rounded-full overflow-hidden" style={{ background: C.s3 }}>
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${Math.min(numValue, 100)}%`,
            background: cl.hex,
            boxShadow: `0 0 10px ${cl.hex}80`,
          }}
        />
      </div>
    </div>
  );
};

// Player Biometrics Panel Component - SLAM Glassmorphism Style
const PlayerBiometricsPanel = ({ playerId }) => {
  const biometrics = MOCK_BIOMETRICS[playerId] || null;

  if (!biometrics) {
    return (
      <GlassCard className="p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: C.text }}>
          <Heart size={20} style={{ color: C.gold, filter: `drop-shadow(0 0 8px ${C.gold}80)` }} />
          <span>Biometrics</span>
        </h2>
        <div className="text-center py-8">
          <WifiOff size={48} className="mx-auto mb-3" style={{ color: C.t3 }} />
          <p className="mb-4" style={{ color: C.t2 }}>No wearables connected</p>
          <p className="text-sm mb-4" style={{ color: C.t3 }}>
            Connect a wearable to track recovery, strain, and sleep metrics
          </p>
          <div className="flex justify-center gap-3">
            <button className="px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-lg text-sm hover:bg-emerald-500/30 transition-all hover:shadow-[0_0_20px_rgba(16,185,129,0.3)] flex items-center gap-2 border border-emerald-500/30">
              <Watch size={16} />
              Connect WHOOP
            </button>
            <button className="px-4 py-2 bg-violet-500/20 text-violet-400 rounded-lg text-sm hover:bg-violet-500/30 transition-all hover:shadow-[0_0_20px_rgba(139,92,246,0.3)] flex items-center gap-2 border border-violet-500/30">
              <Watch size={16} />
              Connect Oura
            </button>
          </div>
        </div>
      </GlassCard>
    );
  }

  const getReadinessColor = (score) => {
    if (score >= 85) return 'green';
    if (score >= 70) return 'yellow';
    return 'red';
  };

  return (
    <GlassCard className="p-6" glow glowColor="rgba(16, 185, 129, 0.15)">
      <DemoModeBanner compact message="Biometrics data is simulated. Connect WHOOP or Oura for live metrics." />
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: C.text }}>
          <Heart size={20} style={{ color: C.gold, filter: `drop-shadow(0 0 8px ${C.gold}80)` }} />
          <span>Biometrics</span>
        </h2>
        <div className="flex items-center gap-2">
          {biometrics.whoopConnected && (
            <span className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/15 px-2.5 py-1 rounded-full border border-emerald-500/30">
              <Wifi size={12} />
              WHOOP
            </span>
          )}
          {biometrics.ouraConnected && (
            <span className="flex items-center gap-1.5 text-xs text-violet-400 bg-violet-500/15 px-2.5 py-1 rounded-full border border-violet-500/30">
              <Wifi size={12} />
              Oura
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <NeonStat
          value={biometrics.readiness}
          label="Readiness"
          color={getReadinessColor(biometrics.readiness)}
          icon={Battery}
        />
        <NeonStat value={biometrics.strain} label="Strain" color="yellow" icon={Zap} />
        <NeonStat value={biometrics.hrv} label="HRV" color="blue" icon={Activity} />
        <NeonStat value={`${biometrics.sleep}h`} label="Sleep" color="purple" icon={Moon} />
      </div>

      <div className="mt-5 flex items-center justify-between text-xs pt-4" style={{ color: C.t3, borderTop: `1px solid ${C.border}` }}>
        <span>Last synced: 2 hours ago</span>
        <button className="flex items-center gap-1.5 transition-colors" style={{ color: C.t3 }}>
          <RefreshCw size={12} />
          Refresh
        </button>
      </div>
    </GlassCard>
  );
};

// Loading skeleton component - SLAM OLED Style
const LoadingSkeleton = () => (
  <div className="space-y-6 animate-pulse">
    {/* Back button skeleton */}
    <div className="h-6 w-32 rounded" style={{ background: C.s2 }} />

    {/* Player header skeleton */}
    <div className="p-6 rounded-[14px]" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
      <div className="flex items-start gap-6">
        <div className="w-24 h-24 rounded-full" style={{ background: C.s2 }} />
        <div className="flex-1 space-y-3">
          <div className="h-8 w-48 rounded" style={{ background: C.s2 }} />
          <div className="h-4 w-32 rounded" style={{ background: C.s2 }} />
          <div className="h-4 w-24 rounded" style={{ background: C.s2 }} />
        </div>
      </div>
    </div>

    {/* Stats grid skeleton */}
    <div className="grid grid-cols-3 gap-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="p-6 rounded-[14px]" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
          <div className="h-4 w-12 rounded mx-auto mb-3" style={{ background: C.s2 }} />
          <div className="h-10 w-20 rounded mx-auto" style={{ background: C.s2 }} />
        </div>
      ))}
    </div>

    {/* Secondary stats skeleton */}
    <div className="grid grid-cols-3 gap-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="p-4 rounded-[14px]" style={{ background: C.s1, border: `1px solid ${C.border}` }}>
          <div className="h-6 w-16 rounded mx-auto mb-2" style={{ background: C.s2 }} />
          <div className="h-3 w-12 rounded mx-auto" style={{ background: C.s2 }} />
        </div>
      ))}
    </div>
  </div>
);

export default function BasketballPlayerPage() {
  const { playerId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const context = useOutletContext();

  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imgError, setImgError] = useState(false);

  // Extract team and league from URL query params
  const teamName = searchParams.get('team');
  const league = searchParams.get('league') || 'NBA';

  // Fetch real player data
  useEffect(() => {
    if (!playerId) {
      setError('No player ID provided');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setImgError(false);

    // If we have team context, fetch the specific player
    if (teamName) {
      getBasketballPlayerById(league, teamName, playerId)
        .then((data) => {
          if (data) {
            setPlayer(data);
          } else {
            setError('Player not found in roster');
          }
        })
        .catch((err) => {
          console.error('Error fetching player:', err);
          setError('Failed to load player data');
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // No team context - show error with helpful message
      setError('Missing team context. Please select a player from the roster.');
      setLoading(false);
    }
  }, [playerId, teamName, league]);

  // Get position-specific stat configuration
  const getPositionCategory = (position) => {
    if (!position) return 'forward';
    const pos = position.split('-')[0]?.trim().toUpperCase();
    if (['PG', 'SG', 'G'].includes(pos)) return 'guard';
    if (['C'].includes(pos)) return 'center';
    return 'forward'; // SF, PF, F default to forward
  };

  // Get position color with glow support
  const getPositionColor = (position) => {
    const pos = position?.split('-')[0]?.trim().toUpperCase();
    return POSITION_COLORS[pos] || { hex: C.gold, bg: `${C.gold}33`, text: C.gold, border: `${C.gold}4D`, glow: 'rgba(232, 184, 0, 0.4)' };
  };

  // Format stat value
  const formatStatValue = (value, format) => {
    if (value === null || value === undefined) return '--';
    if (format === 'pct') return `${value}%`;
    return value;
  };

  // Generate primary and secondary stat cards based on position
  const { primaryStats, secondaryStats } = useMemo(() => {
    if (!player) return { primaryStats: [], secondaryStats: [] };

    const positionCategory = getPositionCategory(player.position);
    const config = POSITION_STATS[positionCategory];

    const primary = config.primary.map((stat) => {
      const value = player.stats?.[stat.key] ?? player[stat.key];
      return {
        key: stat.key,
        label: stat.label,
        value: formatStatValue(value, stat.format),
        icon: stat.icon,
        highlight: stat.highlight,
      };
    });

    const secondary = config.secondary.map((stat) => {
      const value = player.stats?.[stat.key] ?? player[stat.key];
      return {
        key: stat.key,
        label: stat.label,
        value: formatStatValue(value, stat.format),
      };
    });

    return { primaryStats: primary, secondaryStats: secondary };
  }, [player]);

  // Loading state - with ambient glow background
  if (loading) {
    return (
      <div className="min-h-screen ambient-glow-gold p-6">
        <LoadingSkeleton />
      </div>
    );
  }

  // Error state - with glass panel
  if (error) {
    return (
      <div className="min-h-screen ambient-glow-gold flex items-center justify-center p-6">
        <GlassCard className="text-center p-8 max-w-md">
          <AlertCircle size={48} className="mx-auto mb-4" style={{ color: C.gold, filter: `drop-shadow(0 0 12px ${C.gold}80)` }} />
          <p className="mb-6" style={{ color: C.t2 }}>{error}</p>
          <button
            onClick={() => navigate('/basketball/roster-management')}
            className="px-6 py-2.5 font-semibold rounded-lg transition-all"
            style={{ background: C.gold, color: '#000' }}
          >
            Back to Roster
          </button>
        </GlassCard>
      </div>
    );
  }

  // Player not found state - with glass panel
  if (!player) {
    return (
      <div className="min-h-screen ambient-glow-gold flex items-center justify-center p-6">
        <GlassCard className="text-center p-8 max-w-md">
          <User size={48} className="mx-auto mb-4" style={{ color: C.t3 }} />
          <p className="mb-6" style={{ color: C.t2 }}>Player not found</p>
          <button
            onClick={() => navigate('/basketball/roster-management')}
            className="px-6 py-2.5 font-semibold rounded-lg transition-all"
            style={{ background: C.gold, color: '#000' }}
          >
            Back to Roster
          </button>
        </GlassCard>
      </div>
    );
  }

  const posColor = getPositionColor(player.position);
  const hasImage = player.image && !player.image.includes('undefined') && !imgError;

  return (
    <div className="min-h-screen ambient-glow-gold">
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 transition-colors group"
          style={{ color: C.t2 }}
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          Back to Roster
        </button>

        {/* Player Header */}
        <GlassCard className="p-6" glow glowColor={`${C.gold}14`}>
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div
              className="w-28 h-28 rounded-full overflow-hidden flex items-center justify-center"
              style={{ background: C.g08, boxShadow: `0 0 30px ${C.gold}33`, border: `2px solid ${C.goldDim}` }}
            >
              {hasImage ? (
                <img
                  src={player.image}
                  alt={player.name}
                  className="w-full h-full object-cover"
                  onError={() => setImgError(true)}
                />
              ) : (
                <span style={{ color: C.gold, fontWeight: 200, fontSize: 30, textShadow: `0 0 10px ${C.gold}80` }}>
                  #{player.number || player.jersey_number || '?'}
                </span>
              )}
            </div>

            {/* Player Info */}
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <h1 style={{ fontSize: 30, fontWeight: 200, letterSpacing: -0.5, color: C.text }}>
                  {player.name}
                </h1>
                {/* Position badge with glow */}
                <span
                  className="px-3 py-1.5 rounded-full text-xs font-bold"
                  style={{ background: posColor.bg, color: posColor.text, border: `1px solid ${posColor.border}`, textShadow: `0 0 10px ${posColor.glow}` }}
                >
                  {player.position}
                </span>
              </div>
              <p className="mt-2" style={{ color: C.t2 }}>
                {player.team} {league === 'EURO' && <span style={{ color: `${C.gold}B3` }}>• EuroLeague</span>}
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
                {player.height && (
                  <span className="px-3 py-1 rounded-full" style={{ color: C.t2, background: C.s2 }}>
                    {player.height}
                  </span>
                )}
                {player.weight && (
                  <span className="px-3 py-1 rounded-full" style={{ color: C.t2, background: C.s2 }}>
                    {typeof player.weight === 'number' ? `${player.weight} lbs` : player.weight}
                  </span>
                )}
                {player.age && (
                  <span className="px-3 py-1 rounded-full" style={{ color: C.t2, background: C.s2 }}>
                    {player.age} years old
                  </span>
                )}
              </div>
              {/* PIR for EuroLeague */}
              {league === 'EURO' && player.stats?.pir && (
                <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: C.g08, border: `1px solid ${C.goldDim}` }}>
                  <Award size={16} style={{ color: C.gold, filter: `drop-shadow(0 0 6px ${C.gold}80)` }} />
                  <span style={{ color: C.gold, fontWeight: 600, textShadow: `0 0 10px ${C.gold}80` }}>
                    {player.stats.pir} PIR
                  </span>
                </div>
              )}
            </div>
          </div>
        </GlassCard>

        {/* Primary Stats - Large Neon Numbers */}
        <div>
          <h2 className="flex items-center gap-2 mb-3" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 600, color: C.gold }}>
            <TrendingUp size={14} />
            Key Stats
          </h2>
          <div className="grid grid-cols-3 gap-4">
            {primaryStats.map((stat) => (
              <NeonStat
                key={stat.key}
                value={stat.value}
                label={stat.label}
                icon={stat.icon}
                color={stat.highlight ? 'gold' : 'blue'}
                size={stat.highlight ? 'lg' : 'md'}
              />
            ))}
          </div>
        </div>

        {/* Secondary Stats */}
        <div>
          <h2 className="mb-3" style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 600, color: C.t3 }}>
            Secondary Stats
          </h2>
          <div className="grid grid-cols-3 gap-4">
            {secondaryStats.map((stat) => (
              <SecondaryStat key={stat.key} value={stat.value} label={stat.label} />
            ))}
          </div>
        </div>

        {/* Shooting Efficiency & Additional Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Shooting Analysis with Animated Bars */}
          <GlassCard className="p-6">
            <h2 className="text-lg font-semibold mb-5 flex items-center gap-2" style={{ color: C.text }}>
              <Target size={20} style={{ color: C.gold, filter: `drop-shadow(0 0 8px ${C.gold}80)` }} />
              Shooting Efficiency
            </h2>
            <div className="space-y-5">
              <ShootingBar
                label="Field Goals"
                value={player.stats?.fgPct ?? player.fgPct}
                color="gold"
              />
              <ShootingBar
                label="3-Pointers"
                value={player.stats?.threePct ?? player.threePct}
                color="blue"
              />
              <ShootingBar
                label="Free Throws"
                value={player.stats?.ftPct ?? player.ftPct}
                color="green"
              />
            </div>
          </GlassCard>

          {/* Per Game Stats */}
          <GlassCard className="p-6">
            <h2 className="text-lg font-semibold mb-5 flex items-center gap-2" style={{ color: C.text }}>
              <Activity size={20} style={{ color: C.gold, filter: `drop-shadow(0 0 8px ${C.gold}80)` }} />
              Per Game Averages
            </h2>
            <div className="space-y-4">
              {[
                { label: 'Steals', value: player.stats?.spg ?? player.spg },
                { label: 'Blocks', value: player.stats?.bpg ?? player.bpg },
                { label: 'Minutes', value: player.stats?.mpg ?? player.mpg },
              ].map(({ label, value }) => (
                <div key={label} className="flex justify-between items-center py-2" style={{ borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ color: C.t2 }}>{label}</span>
                  <span style={{ color: C.text, fontWeight: 500 }}>{value ?? '--'}</span>
                </div>
              ))}
              {player.role && (
                <div className="flex justify-between items-center py-2 pt-4">
                  <span style={{ color: C.t2 }}>Role</span>
                  <span className="text-right max-w-[60%]" style={{ color: C.gold, fontWeight: 500 }}>
                    {player.role}
                  </span>
                </div>
              )}
            </div>
          </GlassCard>
        </div>

        {/* Biometrics Section */}
        <PlayerBiometricsPanel playerId={player.id} />
      </div>
    </div>
  );
}
